import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation, type Location } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Checkbox,
} from '@chakra-ui/react'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { loginSchema, type LoginFormData } from '@/lib/validations/auth'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'
import { consumePostLoginRedirect, savePostLoginRedirect } from '@/lib/auth/postLoginRedirect'

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const isProcessingRef = useRef(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isLoading } = useAuthStore()

  useEffect(() => {
    const from = (location.state as { from?: Location } | null)?.from
    if (from?.pathname) {
      savePostLoginRedirect(`${from.pathname}${from.search ?? ''}${from.hash ?? ''}`)
    }
  }, [location.state])
  const { settings: siteSettings } = useSiteSettings()
  
  // Theme-aware colors
  const colors = useThemeColors()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    // Prevent multiple submissions
    if (isProcessingRef.current) {
      console.log('[LoginForm] Already processing, ignoring submission')
      return
    }
    
    try {
      isProcessingRef.current = true
      // Clear error when starting new login attempt
      setError('')
      console.log('[LoginForm] Starting login for:', data.email)
      // Include remember_me in login credentials
      const loginData = {
        ...data,
        remember_me: rememberMe,
      }
      
      let result
      try {
        result = await login(loginData)
      } catch (loginError: any) {
        // If login throws an error, handle it here
        console.error('[LoginForm] Login threw error:', loginError)
        console.error('[LoginForm] Login error response:', loginError.response)
        console.error('[LoginForm] Login error response data:', loginError.response?.data)
        
        // Check if error response contains 2FA setup requirement
        if (loginError.response?.status === 403 && loginError.response?.data?.two_factor_setup_required) {
          console.log('[LoginForm] 2FA setup required in error, redirecting')
          const redirectUrl = `/two-factor/setup?email=${encodeURIComponent(data.email)}&remember_me=${rememberMe ? '1' : '0'}`
          window.location.href = redirectUrl
          return
        }
        
        // Extract error message from the thrown error
        const errorData = loginError.response?.data || {}
        console.log('[LoginForm] Error data extracted:', errorData)
        
        let errorMessage = 'Login failed'
        
        // Check for errors array first (common format: { errors: [...], status: false, status_code: 403 })
        if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors[0]
          console.log('[LoginForm] Using errors array:', errorMessage)
        } else if (errorData.message) {
          errorMessage = errorData.message
          console.log('[LoginForm] Using message field:', errorMessage)
        } else if (typeof errorData.errors === 'string') {
          errorMessage = errorData.errors
          console.log('[LoginForm] Using errors string:', errorMessage)
        } else if (loginError.message) {
          errorMessage = loginError.message
          console.log('[LoginForm] Using error message:', errorMessage)
        }
        
        // Handle lockout with remaining time
        if (loginError.response?.status === 429) {
          const lockoutMinutes = errorData.lockout_remaining_minutes
          if (lockoutMinutes) {
            errorMessage = `Too many login attempts. Please try again in ${lockoutMinutes} minute(s).`
          }
        }
        
        console.log('[LoginForm] Setting error message:', errorMessage)
        setError(errorMessage)
        
        isProcessingRef.current = false
        return
      }
      
      // Continue with result handling if login didn't throw
      
      console.log('[LoginForm] Login result received:', result)
      console.log('[LoginForm] Result properties:', {
        success: result?.success,
        twoFactorSetupRequired: result?.twoFactorSetupRequired,
        twoFactorRequired: result?.twoFactorRequired,
        message: result?.message
      })
      
      if (result.success) {
        const next = consumePostLoginRedirect() || '/dashboard'
        console.log('[LoginForm] Login successful, redirecting to', next)
        navigate(next, { replace: true })
      } else if (result.twoFactorSetupRequired) {
        // 2FA setup required (first-time user), redirect to 2FA setup page
        console.log('[LoginForm] 2FA setup required, redirecting to setup page')
        const redirectUrl = `/two-factor/setup?email=${encodeURIComponent(data.email)}&remember_me=${rememberMe ? '1' : '0'}`
        console.log('[LoginForm] Redirect URL:', redirectUrl)
        // Use window.location.href directly for more reliable redirect
        window.location.href = redirectUrl
      } else if (result.twoFactorRequired) {
        // 2FA required (user already has 2FA enabled), redirect to 2FA verification page
        console.log('[LoginForm] 2FA required, redirecting to verification page')
        const redirectUrl = `/two-factor?email=${encodeURIComponent(data.email)}&remember_me=${rememberMe ? '1' : '0'}`
        console.log('[LoginForm] Redirect URL:', redirectUrl)
        // Use window.location.href directly for more reliable redirect
        window.location.href = redirectUrl
      } else {
        // Show error message if login failed for other reasons
        console.log('[LoginForm] Login failed, showing error:', result.message)
        setError(result.message || 'Login failed')
      }
      isProcessingRef.current = false
    } catch (err: any) {
      // This catch block handles any unexpected errors
      console.error('[LoginForm] Unexpected error caught:', err)
      setError(err.message || 'An unexpected error occurred. Please try again.')
      isProcessingRef.current = false
    }
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              {/* Logo - Database driven */}
              <Box mb={2} display="flex" justifyContent="center">
                <img 
                  src={siteSettings.auth_logo || "/assets/img/deltrans-logo.png"} 
                  alt={"Deltrans Logo"} 
                  style={{ maxWidth: '300px', height: 'auto' }}
                />
              </Box>
              
              {/* Sign In Header */}
              <Heading size="lg" color={colors.headerColor} mb={1}>
                {'Sign In'}
              </Heading>
              
              {/* Description */}
              {/* <Text color={colors.text} fontSize="sm">
                {siteSettings.site_description || 'Enter your credentials to access your account'}
              </Text> */}
            </Box>

            {error && error.trim() !== '' ? (
              <Alert 
                key={`error-${error}`} 
                status="error" 
                borderRadius="md" 
                w="100%"
                data-testid="login-error-alert"
              >
                <AlertIcon />
                <AlertDescription fontSize="sm">{error}</AlertDescription>
              </Alert>
            ) : null}

            <Box
              as="form"
              onSubmit={handleSubmit(onSubmit)}
              w="100%"
              noValidate
            >
              <VStack spacing={3} w="100%">
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>Email</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Mail size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('email')}
                      type="email"
                      placeholder="Enter your email"
                      pl="10"
                    />
                  </InputGroup>
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Lock size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      pl="10"
                    />
                    <InputRightElement width="3rem">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        p={0}
                        type="button"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        h="1.75rem"
                        minW="1.75rem"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <Box 
                  mt={1} 
                  w="100%" 
                  display="grid" 
                  gridTemplateColumns="1fr 1fr" 
                  gap={3}
                  alignItems="center"
                >
                  <Box textAlign="left">
                    <Checkbox
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      colorScheme="blue"
                      size="sm"
                    >
                      <Text fontSize="sm" color={colors.link}>
                        Remember me
                      </Text>
                    </Checkbox>
                  </Box>
                  <Box textAlign="right">
                    <Button
                      variant="link"
                      color="blue.500"
                      fontSize="sm"
                      onClick={() => navigate('/forgot-password')}
                      _hover={{ textDecoration: 'underline' }}
                    >
                      Forgot your password?
                    </Button>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  bg={colors.buttonColor}
                  color="white"
                  _hover={{ bg: 'blue.600' }}
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                  loadingText="Signing in..."
                >
                  Login
                </Button>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}

